"use client";

import { Flex, FlexColumn } from "@/components/Flex";
import LazyLoaded from "@/components/LazyLoaded";
import { NotePageContextProvider, useNotePageContextSelector } from "@/components/NotePage/hooks/NotePageContext";
import { SidebarContextProvider, useSidebarContextSelector } from "@/components/NotePage/hooks/SidebarContext";
import Mountable from "@/components/styled/div/Mountable";
import {
    ChatEditorCommunicatorProvider,
    useChatEditorCommunicator,
} from "@/features/AI/KaiChat/hooks/useChatEditorCommunicator";
import SlidingVideoAd from "@/features/Ads/components/SlidingVideoAd";
import { useAuthSlideContextSelector } from "@/features/Auth/AuthSlide/hook/useAuthSlideContext";
import { MoveToPopupProvider } from "@/features/MoveToPopup/hooks/useMoveToPopupContext";
import KaiCompleteUpgradeBanner, {
    KAI_COMPLETE_UPGRADE_BANNER_PREFIX_KEY,
} from "@/features/Payments/components/KaiCompleteUpgradeBanner";
import { useSafeQueryNavigation } from "@/hooks/navigation/useSafeQueryNavigation";
import { useBreakPoints } from "@/hooks/styles/useBreakpoints";
import br from "@/styles/breakpoints.module.css";
import Mixpanel from "@/utils/analytics/Mixpanel";
import { dynamicKeyValueStore } from "@/utils/dynamicKeyValueStore";
import { spacing } from "@/utils/spacing";
import { getHtmlFromContent } from "@knowt/editor/helpers/getHtmlFromContent";
import { Exam, ItemType, Media, Note, SubscriptionType, UserDetails } from "@knowt/syncing/graphql/schema";
import { useMedia } from "@knowt/syncing/hooks/media/useMedia";
import { resolveMediaSWRKey } from "@knowt/syncing/hooks/media/utils";
import { StableSWRKeys } from "@knowt/syncing/hooks/swr/swr";
import { LocalUser } from "@knowt/syncing/hooks/user/types";
import { useCurrentUser, useIfUserSessionExpires } from "@knowt/syncing/hooks/user/useCurrentUser";
import clsx from "clsx";
import dynamic from "next/dynamic";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { unstable_serialize } from "swr";
import SWRConfig from "../ClientSWRConfig";
import { EditorContextProvider } from "../FullPageEditor/hooks/useEditorContextSelector";
import { editorThemeColors } from "../FullPageEditor/utils/editorThemeColors";
import Document from "./Document";
import Header from "./components/Header";
import NotePopups from "./components/popups/Popups";
import NavigationPanel from "./components/sidebar/NavigationPanel";
import QuickInfoPanel from "./components/sidebar/QuickInfoPanel";
import { InnerContainer, MainContainer } from "../Containers";
import { themeColors } from "@/utils/themeColors";
import styles from "./notePage.module.css";

const ReadonlyChatInterface = dynamic(() =>
    import("@/features/AI/KaiChat/ReadonlyChatInterface").then(m => m.ReadonlyChatInterface)
);
const ResponsiveChatAndMediaTabbedView = dynamic(() => import("./components/ResponsiveChatAndMediaTabbedView"));

const NotePageBody = ({ topNotes, examData }: { topNotes?: Note[]; examData?: Exam }) => {
    const openAuthSlide = useAuthSlideContextSelector(state => state.openAuthSlide);

    const noteId = useNotePageContextSelector(state => state.noteId);
    const isNoteOwner = useNotePageContextSelector(state => state.isNoteOwner);
    const note = useNotePageContextSelector(state => state.note);

    const isNoteChatSplitOpen = useSidebarContextSelector(s => s.isNoteChatSplitOpen);

    const { user, hasReachedAIUsage } = useCurrentUser();

    const upgradeEventStorageKey = KAI_COMPLETE_UPGRADE_BANNER_PREFIX_KEY + ItemType.NOTE;

    const [isKaiCompleteUpgradeBannerOpen, setIsKaiCompleteUpgradeBannerOpen] = useState(
        !!user &&
            !dynamicKeyValueStore.get(upgradeEventStorageKey) &&
            (!!hasReachedAIUsage("curPrompts") || !!hasReachedAIUsage("chats"))
    );

    // biome-ignore lint: noExhaustiveDependencies
    useEffect(() => {
        setIsKaiCompleteUpgradeBannerOpen(
            !!user &&
                !dynamicKeyValueStore.get(upgradeEventStorageKey) &&
                (!!hasReachedAIUsage("curPrompts") || !!hasReachedAIUsage("chats"))
        );
    }, [user?.ai]);

    const { media } = useMedia({ mediaId: note?.mediaId });
    const { smDown } = useBreakPoints();

    useIfUserSessionExpires(async (email: string) => {
        openAuthSlide({
            showLoginPage: true,
            extra: {
                prefilledEmail: email,
                onClose: () => location.reload(),
            },
        });
        toast.error("Sorry! For security reasons, you must log back in!");
    });

    const handleKaiCompleteUpgradeBannerClose = () => {
        dynamicKeyValueStore.set(upgradeEventStorageKey, "true");
        setIsKaiCompleteUpgradeBannerOpen(false);
    };

    // biome-ignore lint: useExhaustiveDependencies
    useEffect(() => {
        if (note) {
            Mixpanel.track(`Note - Visited`, {
                noteId: note.noteId,
                public: note.public,
                mediaId: note.mediaId,
                flashcardSetId: note.flashcardSetId,
                isOwner: isNoteOwner,
                exam: note.exam_v2,
                examUnit: note.examUnit,
                subject: note.subject,
                topic: note.topic,
                classId: note.classId,
                xp: user?.xp,
                level: user?.level,
                streak: user?.streak,
                coins: user?.coins,
                gamificationRecords: user?.records,
            });
        }

        // we only want to check this once note ready. `noteId` in deps is enough to check it
    }, [noteId]);

    return (
        <MainContainer
            className={styles.mainContainer}
            style={{
                marginBlock: "0rem 0rem",
                marginInline: "0rem 0rem",
                backgroundColor: themeColors.neutralWhite,
                position: "relative",
            }}
            forceCollapsedSidebar
            noNavBar>
            {/* don't remove, overscroll behavior is showing the other background otherwise */}
            <style data-testid="auto-id-5f163f41-7b5f-4132-ae98-a0ce1bc5408b">{`
                body {
                    background-color: ${editorThemeColors.background};
                }
            `}</style>
            {/* SIDEBAR */}
            {isNoteOwner ? (
                <NavigationPanel style={{ position: "sticky", top: 0 }} />
            ) : (
                <QuickInfoPanel
                    className={clsx(br.mdDownDisplayNone, "scrollbar")}
                    style={{ position: "sticky", top: 0 }}
                    topNotes={topNotes}
                    examData={examData}
                />
            )}
            {/* MAIN CONTENT */}
            <InnerContainer
                className="scrollbar-thin"
                style={{
                    maxWidth: "unset",
                    backgroundColor: editorThemeColors.background,
                    scrollMargin: "6.5rem",
                    flex: 1,
                    margin: 0,
                }}>
                <Header relatedMedia={media} data-testid="auto-id-9110c108-96c7-48ee-9e64-dde01e173c73" />
                <Flex style={{ justifyContent: "space-between", gap: spacing.MD, flex: 1 }} className={br.mdDownColumn}>
                    <FlexColumn style={{ width: "100%", flex: 1 }}>
                        <KaiCompleteUpgradeBanner
                            isOpen={isKaiCompleteUpgradeBannerOpen}
                            onClose={handleKaiCompleteUpgradeBannerClose}
                            itemId={noteId}
                            itemType={ItemType.NOTE}
                        />
                        <Document />

                        <Mountable mount={!isNoteOwner}>
                            <QuickInfoPanel
                                className={br.smUpDisplayNone}
                                style={{
                                    width: "100%",
                                    padding: "1.5rem 2.3rem 3rem 3.3rem",
                                    marginTop: "6.4rem",
                                    height: "auto",
                                }}
                                topNotes={topNotes}
                                examData={examData}
                            />
                        </Mountable>
                    </FlexColumn>

                    <LazyLoaded load={isNoteChatSplitOpen}>
                        <ResponsiveChatAndMediaTabbedView />
                    </LazyLoaded>
                </Flex>
            </InnerContainer>
            <Mountable mount={!isNoteOwner}>
                <ReadonlyChatInterface
                    note={note}
                    floatStyles={{
                        position: "fixed",
                        right: smDown ? "3rem" : "5rem",
                        bottom: smDown ? "3rem" : "5rem",
                        zIndex: 2,
                    }}
                />
            </Mountable>
            <NotePopups />
        </MainContainer>
    );
};

export const EditorContextWrapper = ({ children }: { children: React.ReactNode }) => {
    const note = useNotePageContextSelector(state => state.note);
    const nonEditable = useNotePageContextSelector(state => state.nonEditable);

    const saveContent = useNotePageContextSelector(s => s.saveContent);
    const printDocument = useNotePageContextSelector(s => s.printDocument);
    const toggleNoteChatSplit = useSidebarContextSelector(s => s.toggleNoteChatSplit);
    const collapseNoteSidebar = useSidebarContextSelector(s => s.collapseNoteSidebar);
    const isNoteChatSplitOpen = useSidebarContextSelector(s => s.isNoteChatSplitOpen);
    const focusToChatInput = useChatEditorCommunicator(s => s.focusToChatInput);

    const { addParamsSilentReplace } = useSafeQueryNavigation();

    return (
        <EditorContextProvider
            content={getHtmlFromContent({ content: note?.content ?? "", type: "note" })}
            editable={!nonEditable}
            ssr={typeof window === "undefined"}
            disabledCustomPlaceholder={nonEditable || isNoteChatSplitOpen}
            saveContent={saveContent}
            allowAutoSave={true}
            printDocument={printDocument}
            onAITrigger={() => {
                toggleNoteChatSplit();
                collapseNoteSidebar();
                addParamsSilentReplace({ tab: "Chat" });
                focusToChatInput();
            }}>
            {children}
        </EditorContextProvider>
    );
};

const NotePage = ({
    isNoteOwner,
    initialNote,
    serverUser,
    serverMedia,
    ownerData,
    topNotes,
    examData,
}: {
    isNoteOwner: boolean;
    initialNote: Note;
    serverUser: LocalUser;
    serverMedia?: Media;
    ownerData?: UserDetails;
    topNotes: Note[];
    examData: Exam;
}) => {
    const teacherHasPremium =
        ownerData?.subscriptionType === SubscriptionType.TEACHER_LIMITLESS ||
        ownerData?.subscriptionType === SubscriptionType.TEACHER_PRO;

    return (
        <SWRConfig
            value={{
                fallback: {
                    [StableSWRKeys.USER]: serverUser,
                    [unstable_serialize(["note", initialNote.noteId, serverUser.user?.ID])]: initialNote,
                    [unstable_serialize(resolveMediaSWRKey({ mediaId: serverMedia?.mediaId }))]: serverMedia,
                },
            }}>
            <ChatEditorCommunicatorProvider itemId={initialNote.noteId} itemType={ItemType.NOTE} isOwner={isNoteOwner}>
                <SidebarContextProvider mediaId={initialNote.mediaId} noteId={initialNote.noteId}>
                    <NotePageContextProvider
                        isNoteOwner={isNoteOwner}
                        serverUser={serverUser}
                        initialNote={initialNote}
                        ownerData={ownerData}>
                        <MoveToPopupProvider>
                            <EditorContextWrapper>
                                <NotePageBody topNotes={topNotes} examData={examData} />
                                <SlidingVideoAd isEnabled={!teacherHasPremium} />
                            </EditorContextWrapper>
                        </MoveToPopupProvider>
                    </NotePageContextProvider>
                </SidebarContextProvider>
            </ChatEditorCommunicatorProvider>
        </SWRConfig>
    );
};

export default NotePage;
